/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { FluentProvider, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons';
import { Layout } from './components/chrome';
import { Dashboard } from './components/pages';
import { useThemeService } from './data/services/useThemeService';
import { useCaseService } from './data/services';
import { ClientPortal, ClientPortalUser } from './data/types';
import { appTokens } from './components/common/Styles/appTokens';
import { useAccountLevelSignalR } from './signalR/useAccountLevelSignalR';
import { SigninOidc } from './components/pages/Auth/SigninOidc';
import { AuthManager } from './auth/authManager';
import { useAuthState } from './redux/reducers/authReducer';
import { useAuthActions } from './redux/actions/authActions';
import { useAccountActions } from './redux/actions/accountActions';
import './App.css';
import { useClientPortalService } from './data/services/useClientPortalService';
import { useUserAccountService } from './data/services/useUserAccountService';

const PORTAL_PERMISSION_ERROR =
    'You do not have permissions for this portal. Please contact your account administrator.';
const MISSING_PORTAL_ERROR = 'The URL is missing a portal ID. Please use your portal link to log in.';
const LOGIN_ERROR = 'There was an error logging in. Please try again.';

const getPortalIdFromPath = (pathname: string): string | undefined => {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length !== 1 || pathSegments[0] === 'signin-oidc') {
        return undefined;
    }

    try {
        return decodeURIComponent(pathSegments[0]);
    } catch {
        return undefined;
    }
};

const useAppStyles = makeStyles({
    allDivScroll: {
        '& div': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${tokens.colorNeutralForeground4} ${tokens.colorNeutralBackground2}`,
        },
    },
});

export const App = () => {
    //Hooks
    const location = useLocation();
    const portalId = getPortalIdFromPath(location.pathname);
    const authState = useAuthState();
    const authActions = useAuthActions();
    const accountActions = useAccountActions();
    const authManager = AuthManager.getInstance(authActions);
    const caseService = useCaseService();

    //State
    const [isAuthInitializing, setIsAuthInitializing] = useState<boolean>(true);
    const [isWorkspaceInitializing, setIsWorkspaceInitializing] = useState<boolean>(true);
    const [loginError, setLoginError] = useState<string>();
    const [portal, setPortal] = useState<ClientPortal>();
    const [portalUser, setPortalUser] = useState<ClientPortalUser>();

    //Services
    const themeService = useThemeService();

    const portalService = useClientPortalService(portalId);
    const userAccountService = useUserAccountService();

    //SignalR Hooks
    useAccountLevelSignalR();

    //Styles
    const styles = useAppStyles();
    const rootClasses = mergeClasses(styles.allDivScroll);

    //Initializers
    useEffect(() => {
        initializeFileTypeIcons(/* optional base url */);
    }, []);

    useEffect(() => {
        let isDisposed = false;
        const controller = new AbortController();
        const isSignInCallback = location.pathname === '/signin-oidc';
        const loginAttempted = location.state?.loginAttempted === true;

        const denyPortalAccess = (error?: string) => {
            setLoginError(error ?? (loginAttempted ? PORTAL_PERMISSION_ERROR : undefined));
            setPortal(undefined);
            setPortalUser(undefined);
            portalService.clearPortals();
            userAccountService.clearAccount();
            authManager.clearTokens();
        };

        const initializePortal = async () => {
            setLoginError(location.state?.loginError);
            setPortal(undefined);
            setPortalUser(undefined);
            setIsAuthInitializing(true);
            setIsWorkspaceInitializing(true);
            userAccountService.clearAccount();
            portalService.clearPortals();

            if (!portalId) {
                denyPortalAccess(MISSING_PORTAL_ERROR);
                setIsAuthInitializing(false);
                setIsWorkspaceInitializing(false);
                return;
            }

            let authenticatedUserId: string;
            try {
                authenticatedUserId = await authManager.checkAuthentication();
                if (isDisposed) return;
            } catch (error) {
                if (isDisposed) return;
                console.error('Authentication check failed', error);
                authManager.clearTokens();
                accountActions.clearAccounts();
                accountActions.clearPrimaryAccount();
                if (!isDisposed) {
                    if (loginAttempted) {
                        setLoginError(location.state?.loginError ?? LOGIN_ERROR);
                    }
                    setIsAuthInitializing(false);
                    setIsWorkspaceInitializing(false);
                }
                return;
            } finally {
                if (!isDisposed) {
                    setIsAuthInitializing(false);
                }
            }

            try {
                const requestedPortal = await portalService.getClientPortalAsync();
                if (isDisposed) return;
                if (
                    !requestedPortal
                    || requestedPortal.portalId !== portalId
                    || !requestedPortal.isEnabled
                    || requestedPortal.lifecycle?.isDeleted
                ) {
                    denyPortalAccess();
                    return;
                }

                const requestedPortalId = requestedPortal.portalId ?? portalId;
                const requestedPortalUser = await portalService.getClientPortalUserAsync(
                    authenticatedUserId,
                    requestedPortalId,
                );
                if (isDisposed) return;
                if (
                    !requestedPortalUser
                    || requestedPortalUser.userId !== authenticatedUserId
                    || requestedPortalUser.portalId !== requestedPortalId
                    || requestedPortalUser.lifecycle?.isDeleted
                ) {
                    denyPortalAccess();
                    return;
                }

                await userAccountService.loadPortalAccountAsync(
                    requestedPortal,
                    requestedPortalUser,
                    controller.signal,
                );
                await caseService.getCaseAsync(requestedPortal.accountId!, requestedPortal.caseId!);
                if (isDisposed) return;

                setPortal(requestedPortal);
                setPortalUser(requestedPortalUser);
            } catch (error) {
                if (isDisposed) return;
                console.error('Workspace initialization failed', error);
                denyPortalAccess();
            } finally {
                if (!isDisposed) {
                    setIsWorkspaceInitializing(false);
                }
            }
        };

        if (!isSignInCallback) {
            void initializePortal();
        }

        return () => {
            isDisposed = true;
            controller.abort();
        };
    }, [portalId, location.pathname, location.search, location.state]);

    // This effect is intentionally keyed to auth bootstrap + user auth state.
    useEffect(() => {
        if (isAuthInitializing || !authState.userId) {
            return;
        }

        let isDisposed = false;

        const refreshSession = async () => {
            if (isDisposed || !authManager.isAccessTokenNearExpiry()) {
                return;
            }

            const recovered = await authManager.tryReauthenticate();
            if (!recovered) {
                authManager.clearTokens();
                accountActions.clearAccounts();
                accountActions.clearPrimaryAccount();
            }
        };

        const intervalId = window.setInterval(() => {
            void refreshSession();
        }, 60000);

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void refreshSession();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            isDisposed = true;
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isAuthInitializing, authState.userId]);

    //Effects
    const mqListener = (mql: any) => {
        const prefersDark = !!mql.matches;
        if (themeService.isAutoTheme) {
            themeService.setToAutoTheme(prefersDark);
        }

        // Update favicon based on theme
        const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
        if (favicon) {
            favicon.href = prefersDark ? '/white-icon.png' : '/blue-icon.png';
        }
    };
    // This effect intentionally subscribes once to system theme changes.
    useEffect(() => {
        const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)');
        darkThemeMq.addEventListener('change', mqListener);

        return () => darkThemeMq.removeEventListener('change', mqListener);
    }, []);

    useEffect(() => {
        document.body.style.backgroundColor = themeService.isDarkTheme ? '#192134' : '#fafafa';
    }, [themeService.isDarkTheme]);

    return (
        <FluentProvider theme={themeService.isDarkTheme ? appTokens.themes.darkTheme : appTokens.themes.lightTheme}>
                <div className={rootClasses}>
                    <Routes>
                        <Route path='/signin-oidc' Component={SigninOidc} />
                        <Route
                            path='/*'
                            element={
                                <StandardRoutes
                                    isInitializing={
                                        isAuthInitializing
                                        || isWorkspaceInitializing
                                        || (!!authState.userId && (!portal || !portalUser))
                                    }
                                    loginError={loginError}
                                    hasPortalAccess={!!portal && !!portalUser}
                                />
                            }
                        />
                    </Routes>
                </div>
        </FluentProvider>
    );
};

const MissingCaseRedirect = () => {
    return null;
};

type StandardRoutesProps = {
    isInitializing: boolean;
    loginError?: string;
    hasPortalAccess: boolean;
};

const StandardRoutes = ({ isInitializing, loginError, hasPortalAccess }: StandardRoutesProps) => {
    return (
        <Layout
            isInitializing={isInitializing}
            loginError={loginError}
            hasPortalAccess={hasPortalAccess}
        >
            <Routes>
                <Route path='*'>
                    <Route path=':caseId' Component={Dashboard} />
                    <Route path='*' Component={MissingCaseRedirect} />
                </Route>
            </Routes>
        </Layout>
    );
};
